import React from "react";
import { ConfigProvider, FloatButton, Tooltip } from "antd";
import classNames from "classnames";
import styles from "./style.module.scss";

const PREDEFINED_COLORS = ["primary", "danger", "warning", "success"];

export interface VLFloatButtonProps {
  onClick: any;
  icon: React.ReactNode;
  color?: "primary" | "danger" | "warning" | "success" | `#${string}` | string; // predefined colors or custom hex colors, defaults to 'primary'
  tooltip?: string; // tooltip text to display on hover
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
  buttonSize?: "medium" | "large";
}

const VLFloatButton: React.FC<VLFloatButtonProps> = ({
  onClick,
  icon,
  color,
  tooltip,
  tooltipPlacement = "top",
  buttonSize = "medium",
}) => {
  const finalColor = color || "primary";

  // Check if it's a predefined color name
  const isPredefinedColor = PREDEFINED_COLORS.includes(finalColor);

  // Use classNames similar to VLButton pattern
  const wrapperClass = classNames(styles.vlFloatButtonWrapper, {
    [styles.primary]: isPredefinedColor && finalColor === "primary",
    [styles.danger]: isPredefinedColor && finalColor === "danger",
    [styles.warning]: isPredefinedColor && finalColor === "warning",
    [styles.success]: isPredefinedColor && finalColor === "success",
    [styles.customColor]: !isPredefinedColor,
    [styles[buttonSize]]: buttonSize,
  });

  const floatButton = (
    <FloatButton
      shape="square"
      icon={icon}
      onClick={onClick}
      className={classNames(wrapperClass, "ant-float-btn-pure")}
      data-testid="vl-float-button"
    />
  );

  return (
    <div
      className={styles.wrapper}
      data-testid="vl-float-button-wrapper"
      style={
        !isPredefinedColor
          ? ({ "--vl-custom-bg": finalColor } as React.CSSProperties)
          : undefined
      }
    >
      <ConfigProvider
        theme={{
          components: {
            Button: {
              defaultHoverBorderColor: "inherit",
              defaultActiveBorderColor: "inherit",
            },
          },
        }}
      >
        {tooltip ? (
          <Tooltip
            title={tooltip}
            placement={tooltipPlacement}
            data-testid="vl-float-button-tooltip"
          >
            {floatButton}
          </Tooltip>
        ) : (
          floatButton
        )}
      </ConfigProvider>
    </div>
  );
};

export default VLFloatButton;
